import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  CheckCircle as CompleteIcon,
  Print as PrintIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import {
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box
} from '@mui/material';
import { styled } from '@mui/material/styles';
import api from '../../services/api';
import usePageTitle from '../../utils/usePageTitle';
import './WorkOrderDetails.css';

const ModernTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: '#fafbfc',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: '#f5f6f7',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px'
      }
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
        boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`
      }
    }
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    '&.Mui-focused': { color: theme.palette.primary.main, fontWeight: 600 }
  }
}));

const WorkOrderDetails = () => {
  const { projectCode } = useParams();
  usePageTitle(`Work Order - ${projectCode}`);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [modifiedAlert, setModifiedAlert] = useState(false);
  const [drNotCompleted, setDrNotCompleted] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    if (success) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);
  
  const [models, setModels] = useState([]);
  const [picList, setPicList] = useState([]);
  const [jobLinesList, setJobLinesList] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [typeOfWork, setTypeOfWork] = useState('');
  
  const [workOrderData, setWorkOrderData] = useState({
    header: {
      modelDetailId: '',
      projectCode: '',
      quantity: '',
      startDate: '',
      endDate: '',
      picName: '',
      lineName: '',
      factory: '',
      remarks: '',
      technician: '',
      completionDate: '',
      status: 'ACT',
      modifiedDate: '',
      comments: ''
    },
    inventoryStatusChangeList: [],
    inventoryTotals: { aAvlTotal: 0, bAvlTotal: 0, eAvlTotal: 0, qahdTotal: 0, total: 0 }
  });
  
  const [selectedInvnRows, setSelectedInvnRows] = useState([]);
  const [expandedSections, setExpandedSections] = useState({ inventory: true });

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const [modelsRes, picRes, jobLinesRes, typeRes] = await Promise.all([
        api.get(`/work-order/models/${projectCode}`),
        api.get(`/work-order/pic/${projectCode}`),
        api.get(`/work-order/jobline/${projectCode}`),
        api.get(`/work-order/typeofwork/${projectCode}`)
      ]);
      
      setModels(modelsRes.data || []);
      setPicList(picRes.data || []);
      setJobLinesList(jobLinesRes.data || []);
      setTypeOfWork(typeRes.data?.typeOfWork || '');
      
      if (modelsRes.data && modelsRes.data.length > 0) {
        setSelectedModelId(modelsRes.data[0].model_dtl_id || modelsRes.data[0].id);
      }
    } catch (err) {
      setError('Failed to load data: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [projectCode]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const calculateInventoryTotals = useCallback((inventoryList) => {
    console.log('=== Calculate Inventory Totals ===');
    console.log('Input inventoryList:', JSON.stringify(inventoryList, null, 2));
    
    const totals = {
      aAvlTotal: 0,
      bAvlTotal: 0,
      eAvlTotal: 0,
      qahdTotal: 0,
      total: 0
    };
    
    inventoryList.forEach((item, idx) => {
      console.log(`Row ${idx}: to=${item.to}, quantity=${item.quantity}, type=${typeof item.quantity}`);
      const qty = Number(item.quantity) || 0;
      console.log(`Row ${idx}: Converted qty=${qty}, type=${typeof qty}`);
      
      if (item.to === 'A') {
        console.log(`Adding ${qty} to aAvlTotal (was ${totals.aAvlTotal})`);
        totals.aAvlTotal = totals.aAvlTotal + qty;
        console.log(`aAvlTotal now: ${totals.aAvlTotal}`);
      }
      else if (item.to === 'B') {
        console.log(`Adding ${qty} to bAvlTotal (was ${totals.bAvlTotal})`);
        totals.bAvlTotal = totals.bAvlTotal + qty;
        console.log(`bAvlTotal now: ${totals.bAvlTotal}`);
      }
      else if (item.to === 'E') {
        console.log(`Adding ${qty} to eAvlTotal (was ${totals.eAvlTotal})`);
        totals.eAvlTotal = totals.eAvlTotal + qty;
        console.log(`eAvlTotal now: ${totals.eAvlTotal}`);
      }
      else if (item.to === 'Q') {
        console.log(`Adding ${qty} to qahdTotal (was ${totals.qahdTotal})`);
        totals.qahdTotal = totals.qahdTotal + qty;
        console.log(`qahdTotal now: ${totals.qahdTotal}`);
      }
    });
    
    totals.total = totals.aAvlTotal + totals.bAvlTotal + totals.eAvlTotal;
    console.log('Final totals:', totals);
    console.log('=================================');
    return totals;
  }, []);

  const loadWorkOrderData = useCallback(async (modelId, keepSavedState = false) => {
    try {
      const response = await api.get(`/work-order/${modelId}`);
      const data = response.data;
      
      if (data.inventoryStatusChangeList) {
        data.inventoryStatusChangeList = data.inventoryStatusChangeList.map(item => ({
          ...item,
          quantity: Number(item.quantity) || 0
        }));
      }
      
      if (data.inventoryStatusChangeList && data.inventoryStatusChangeList.length > 0) {
        const newTotals = calculateInventoryTotals(data.inventoryStatusChangeList);
        data.inventoryTotals = newTotals;
      }
      
      setWorkOrderData(data);
      if (!keepSavedState) {
        setIsSaved(false);
      }
    } catch (err) {
      console.error('Load work order error:', err);
      setError('Failed to load work order data');
    }
  }, [calculateInventoryTotals]);

  const checkModifiedAlert = useCallback(async (modelId) => {
    try {
      const response = await api.get(`/work-order/modified-alert/${modelId}`);
      setModifiedAlert(response.data?.modified || false);
    } catch (err) {
      console.error('Check modified alert error:', err);
    }
  }, []);

  const checkDRStatus = useCallback(async () => {
    try {
      const response = await api.get(`/work-order/check-dr-status/${projectCode}`);
      setDrNotCompleted(!response.data?.isConfirmed);
    } catch (err) {
      console.error('Check DR status error:', err);
    }
  }, [projectCode]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedModelId) {
      loadWorkOrderData(selectedModelId);
      checkModifiedAlert(selectedModelId);
      checkDRStatus();
    }
  }, [selectedModelId, loadWorkOrderData, checkModifiedAlert, checkDRStatus]);

  const handleModelChange = (event) => {
    setSelectedModelId(event.target.value);
  };

  const handleHeaderChange = (field, value) => {
    setWorkOrderData(prev => ({
      ...prev,
      header: { ...prev.header, [field]: value }
    }));
    // Don't set isSaved to false if only technician field is changed
    if (field !== 'technician') {
      setIsSaved(false);
    }
  };

  const handleInventoryChange = (index, field, value) => {
    setWorkOrderData(prev => {
      const newList = [...prev.inventoryStatusChangeList];
      const numValue = field === 'quantity' ? (Number(value) || 0) : value;
      newList[index] = { ...newList[index], [field]: numValue };
      const newTotals = calculateInventoryTotals(newList);
      return { ...prev, inventoryStatusChangeList: newList, inventoryTotals: newTotals };
    });
    setIsSaved(false);
  };

  const addInventoryRows = () => {
    setWorkOrderData(prev => {
      const newList = [
        ...prev.inventoryStatusChangeList,
        { id: 0, from: '', to: '', quantity: 0, dayStatus: 'T', status: 'ACT' },
        { id: 0, from: '', to: '', quantity: 0, dayStatus: 'T', status: 'ACT' }
      ];
      const newTotals = calculateInventoryTotals(newList);
      return { ...prev, inventoryStatusChangeList: newList, inventoryTotals: newTotals };
    });
  };

  const deleteInventoryRows = () => {
    setWorkOrderData(prev => {
      const newList = prev.inventoryStatusChangeList.filter(
        (_, index) => !selectedInvnRows.includes(index)
      );
      const newTotals = calculateInventoryTotals(newList);
      return { ...prev, inventoryStatusChangeList: newList, inventoryTotals: newTotals };
    });
    setSelectedInvnRows([]);
  };

  const handleSave = async () => {
    try {
      // Validation
      if (!workOrderData.header.startDate) {
        setError('Please select Start Date');
        return;
      }
      if (!workOrderData.header.endDate) {
        setError('Please select End Date');
        return;
      }
      if (!workOrderData.header.picName) {
        setError('Please select Person In Charge');
        return;
      }
      // Technician is optional for save
      
      setSaving(true);
      setError('');
      setSuccess('');
      
      await api.post('/work-order/save', workOrderData);
      setSuccess('SAVED - Work order saved successfully.');
      setIsSaved(true);
      loadWorkOrderData(selectedModelId, true);
    } catch (err) {
      setError('Failed to save work order. Please check all required fields.');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    try {
      // Validation
      if (!workOrderData.header.startDate) {
        setError('Please select Start Date');
        return;
      }
      if (!workOrderData.header.endDate) {
        setError('Please select End Date');
        return;
      }
      if (!workOrderData.header.picName) {
        setError('Please select Person In Charge');
        return;
      }
      if (!workOrderData.header.technician || workOrderData.header.technician.trim() === '') {
        setError('Please enter Technician');
        return;
      }
      if (workOrderData.inventoryStatusChangeList.length === 0) {
        setError('Please add at least one inventory status change');
        return;
      }
      
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Save data first, then complete
      await api.post('/work-order/save', workOrderData);
      await api.post('/work-order/complete', { modelDetailId: selectedModelId });
      setSuccess('COMPLETED - Work order completed successfully.');
      
      // Reload models list to reflect updated status
      await loadInitialData();
      loadWorkOrderData(selectedModelId);
    } catch (err) {
      setError('Failed to complete work order. Please check all required fields.');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const isEditable = workOrderData.header.status === 'ACT' && typeOfWork !== 'RBX';

  if (loading) {
    return (
      <div className="work-order-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="work-order-container">
      <div className="action-bar no-print">
        <button className="btn-primary" onClick={handleSave} disabled={!isEditable || saving} aria-label="Save work order">
          <SaveIcon fontSize="small" aria-hidden="true" /> Save
        </button>
        <button className="btn-success" onClick={handleComplete} disabled={!isEditable || saving || !isSaved} aria-label="Complete work order">
          <CompleteIcon fontSize="small" aria-hidden="true" /> Complete
        </button>
        <button className="btn-secondary" onClick={handlePrint} aria-label="Print work order">
          <PrintIcon fontSize="small" aria-hidden="true" /> Print
        </button>
        <button className="btn-secondary" onClick={() => navigate('/work-order')} aria-label="Go back to work orders list">
          <BackIcon fontSize="small" aria-hidden="true" /> Back
        </button>
      </div>

      <header className="work-order-header">
        <h1 className="work-order-title">WORK ORDER</h1>
        <h2 className="work-order-subtitle">Sony Electronics Inc.</h2>
      </header>

      <div className="project-code-section">
        <strong>Project Code: </strong>{projectCode}
      </div>

      {error && <Alert severity="error" sx={{ mb: 2, position: 'sticky', top: 0, zIndex: 1000 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, position: 'sticky', top: 0, zIndex: 1000 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {modifiedAlert && <Alert severity="warning" sx={{ mb: 2 }}>The Customer Request Form has been updated.</Alert>}
      {drNotCompleted && <Alert severity="warning" sx={{ mb: 2 }}>Please complete the Delivery Request before proceeding with the Work Order.</Alert>}

      <div className="work-order-content">
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 3 }}>
          <FormControl fullWidth required>
            <InputLabel>Model</InputLabel>
            <Select
              value={selectedModelId || ''}
              onChange={handleModelChange}
              label="Model"
              sx={{ borderRadius: 3, backgroundColor: '#fafbfc' }}
            >
              {models.map((model) => (
                <MenuItem key={model.model_dtl_id || model.id} value={model.model_dtl_id || model.id}>
                  {model.from || (model.from_model && model.to_model ? `From ${model.from_model} To ${model.to_model}` : model.from_model)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <ModernTextField
            fullWidth
            label="Quantity"
            value={workOrderData.header.quantity}
            disabled
          />
          
          <ModernTextField
            fullWidth
            label="Project Code"
            value={projectCode}
            disabled
          />
          
          <ModernTextField
            fullWidth
            required
            label="Start Date"
            type="datetime-local"
            value={workOrderData.header.startDate}
            onChange={(e) => {
              handleHeaderChange('startDate', e.target.value);
              e.target.blur();
            }}
            disabled={!isEditable}
            InputLabelProps={{ shrink: true }}
          />
          
          <ModernTextField
            fullWidth
            required
            label="End Date"
            type="datetime-local"
            value={workOrderData.header.endDate}
            onChange={(e) => {
              handleHeaderChange('endDate', e.target.value);
              e.target.blur();
            }}
            disabled={!isEditable}
            InputLabelProps={{ shrink: true }}
          />
          
          {isEditable ? (
            <FormControl fullWidth required>
              <InputLabel>Person In Charge</InputLabel>
              <Select
                value={workOrderData.header.picName || ''}
                onChange={(e) => handleHeaderChange('picName', e.target.value)}
                label="Person In Charge"
                sx={{ borderRadius: 3, backgroundColor: '#fafbfc' }}
              >
                <MenuItem value="">Select</MenuItem>
                {picList.map((pic) => (
                  <MenuItem key={pic.name} value={pic.name}>{pic.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <ModernTextField
              fullWidth
              required
              label="Person In Charge"
              value={workOrderData.header.picName || ''}
              disabled
            />
          )}
          
          {isEditable ? (
            <FormControl fullWidth>
              <InputLabel>Job Line</InputLabel>
              <Select
                value={workOrderData.header.lineName || ''}
                onChange={(e) => handleHeaderChange('lineName', e.target.value)}
                label="Job Line"
                sx={{ borderRadius: 3, backgroundColor: '#fafbfc' }}
              >
                <MenuItem value="">Select</MenuItem>
                {jobLinesList.map((line) => (
                  <MenuItem key={line.name} value={line.name}>{line.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <ModernTextField
              fullWidth
              label="Job Line"
              value={workOrderData.header.lineName || ''}
              disabled
            />
          )}
          
          <ModernTextField
            fullWidth
            label="Factory"
            value={workOrderData.header.factory}
            onChange={(e) => handleHeaderChange('factory', e.target.value)}
            disabled={!isEditable}
            inputProps={{ maxLength: 50 }}
          />
          
          <Box sx={{ gridColumn: '1 / -1' }}>
            <ModernTextField
              fullWidth
              multiline
              rows={4}
              label="Remarks (Max 4000 characters)"
              value={workOrderData.header.remarks}
              onChange={(e) => handleHeaderChange('remarks', e.target.value)}
              disabled={!isEditable}
              inputProps={{ maxLength: 4000 }}
            />
          </Box>
        </Box>
      </div>

      <div className="inventory-section">
        <div className="section-header">
          <h3 className="section-title">Inventory Status Change</h3>
          <div className="section-actions no-print">
            <button className="btn-primary" onClick={addInventoryRows} disabled={!isEditable} aria-label="Add inventory row">
              <AddIcon fontSize="small" aria-hidden="true" /> Add Row
            </button>
            <button className="btn-danger" onClick={deleteInventoryRows} disabled={!isEditable || selectedInvnRows.length === 0} aria-label="Delete selected inventory rows">
              <DeleteIcon fontSize="small" aria-hidden="true" /> Delete Row
            </button>
            <button className="btn-icon" onClick={() => setExpandedSections(prev => ({ ...prev, inventory: !prev.inventory }))} aria-label={expandedSections.inventory ? 'Collapse inventory section' : 'Expand inventory section'} aria-expanded={expandedSections.inventory}>
              {expandedSections.inventory ? <ExpandLessIcon aria-hidden="true" /> : <ExpandMoreIcon aria-hidden="true" />}
            </button>
          </div>
        </div>

        {expandedSections.inventory && (
          <div className="inventory-content">
            <div className="inventory-table-wrapper">
              <table className="inventory-table" aria-label="Inventory status change table">
                <thead>
                  <tr>
                    <th rowSpan="2">
                      <input 
                        type="checkbox" 
                        className="checkbox-input"
                        aria-label="Select all inventory rows"
                        checked={workOrderData.inventoryStatusChangeList.length > 0 && selectedInvnRows.length === workOrderData.inventoryStatusChangeList.length} 
                        onChange={(e) => { if (e.target.checked) { setSelectedInvnRows(workOrderData.inventoryStatusChangeList.map((_, i) => i)); } else { setSelectedInvnRows([]); }}} 
                        disabled={!isEditable}
                      />
                    </th>
                    <th colSpan="4">From</th>
                    <th colSpan="4">To</th>
                    <th rowSpan="2">Quantity</th>
                  </tr>
                  <tr>
                    <th>A-Avl</th>
                    <th>B-Avl</th>
                    <th>E-Avl</th>
                    <th>QAHD</th>
                    <th>A-Avl</th>
                    <th>B-Avl</th>
                    <th>E-Avl</th>
                    <th>QAHD</th>
                  </tr>
                </thead>
                <tbody>
                  {workOrderData.inventoryStatusChangeList.map((row, index) => (
                    <tr key={index}>
                      <td className="checkbox-cell">
                        <input 
                          type="checkbox" 
                          className="checkbox-input"
                          aria-label={`Select inventory row ${index + 1}`}
                          checked={selectedInvnRows.includes(index)} 
                          onChange={() => { setSelectedInvnRows(prev => prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]); }} 
                          disabled={!isEditable}
                        />
                      </td>
                      <td className="radio-cell"><input type="radio" className="radio-input" aria-label={`From A-Available for row ${index + 1}`} checked={row.from === 'A'} onChange={() => handleInventoryChange(index, 'from', 'A')} disabled={!isEditable} /></td>
                      <td className="radio-cell"><input type="radio" className="radio-input" aria-label={`From B-Available for row ${index + 1}`} checked={row.from === 'B'} onChange={() => handleInventoryChange(index, 'from', 'B')} disabled={!isEditable} /></td>
                      <td className="radio-cell"><input type="radio" className="radio-input" aria-label={`From E-Available for row ${index + 1}`} checked={row.from === 'E'} onChange={() => handleInventoryChange(index, 'from', 'E')} disabled={!isEditable} /></td>
                      <td className="radio-cell"><input type="radio" className="radio-input" aria-label={`From QAHD for row ${index + 1}`} checked={row.from === 'Q'} onChange={() => handleInventoryChange(index, 'from', 'Q')} disabled={!isEditable} /></td>
                      <td className="radio-cell"><input type="radio" className="radio-input" aria-label={`To A-Available for row ${index + 1}`} checked={row.to === 'A'} onChange={() => handleInventoryChange(index, 'to', 'A')} disabled={!isEditable} /></td>
                      <td className="radio-cell"><input type="radio" className="radio-input" aria-label={`To B-Available for row ${index + 1}`} checked={row.to === 'B'} onChange={() => handleInventoryChange(index, 'to', 'B')} disabled={!isEditable} /></td>
                      <td className="radio-cell"><input type="radio" className="radio-input" aria-label={`To E-Available for row ${index + 1}`} checked={row.to === 'E'} onChange={() => handleInventoryChange(index, 'to', 'E')} disabled={!isEditable} /></td>
                      <td className="radio-cell"><input type="radio" className="radio-input" aria-label={`To QAHD for row ${index + 1}`} checked={row.to === 'Q'} onChange={() => handleInventoryChange(index, 'to', 'Q')} disabled={!isEditable} /></td>
                      <td><input type="number" className="quantity-input" aria-label={`Quantity for row ${index + 1}`} value={row.quantity || ''} onChange={(e) => handleInventoryChange(index, 'quantity', e.target.value)} disabled={!isEditable} min="0" /></td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td colSpan="5" style={{textAlign: 'right', fontWeight: 'bold'}}>Total:</td>
                    <td>{workOrderData.inventoryTotals?.aAvlTotal || 0}</td>
                    <td>{workOrderData.inventoryTotals?.bAvlTotal || 0}</td>
                    <td>{workOrderData.inventoryTotals?.eAvlTotal || 0}</td>
                    <td>{workOrderData.inventoryTotals?.qahdTotal || 0}</td>
                    <td>{workOrderData.inventoryTotals?.total || 0}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="work-order-content">
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <ModernTextField
              fullWidth
              multiline
              rows={3}
              label="Comments (Max 200 characters)"
              value={workOrderData.header.comments}
              onChange={(e) => handleHeaderChange('comments', e.target.value)}
              disabled={!isEditable}
              inputProps={{ maxLength: 200 }}
            />
          </Box>
          
          <ModernTextField
            fullWidth
            label="Technician"
            value={workOrderData.header.technician}
            onChange={(e) => handleHeaderChange('technician', e.target.value)}
            disabled={!isEditable}
            inputProps={{ maxLength: 50 }}
          />
          
          <ModernTextField
            fullWidth
            label="Completion Date"
            value={workOrderData.header.completionDate}
            disabled
          />
        </Box>
      </div>
    </div>
  );
};

export default WorkOrderDetails;
